import { useState } from 'react';
import { TouchableOpacity, Text, StyleSheet, Alert, Platform, ActivityIndicator } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Swords } from 'lucide-react-native';
import { hapticsUtil } from '@/lib/haptics';

interface RematchButtonProps {
  opponentId: string;
  exerciseId: string;
  timeframeHours?: number;
  duelType?: 'pr' | 'volume' | '1rm' | 'consistency' | 'strength';
  onRematchCreated?: () => void;
}

export default function RematchButton({
  opponentId,
  exerciseId,
  timeframeHours = 24,
  duelType = 'pr',
  onRematchCreated,
}: RematchButtonProps) {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const [creating, setCreating] = useState(false);

  const handleRematch = () => {
    const doCreate = async () => {
      setCreating(true);
      try {
        hapticsUtil.buttonPress();
        const { error } = await supabase.from('duels').insert({
          challenger_id: profile!.id,
          challenged_id: opponentId,
          exercise_id: exerciseId,
          duel_type: duelType,
          timeframe_hours: timeframeHours,
          status: 'pending',
        });
        if (error) throw error;
        hapticsUtil.duelCreated();
        onRematchCreated?.();
      } catch (err: any) {
        const msg = err?.message || 'Failed to create rematch';
        Platform.OS === 'web' ? alert(msg) : Alert.alert('Error', msg);
      } finally {
        setCreating(false);
      }
    };

    if (Platform.OS === 'web') {
      if (confirm('Send a rematch challenge?')) doCreate();
    } else {
      Alert.alert('Rematch', 'Send a rematch challenge?', [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Send', onPress: doCreate },
      ]);
    }
  };

  return (
    <TouchableOpacity
      style={[styles.button, { backgroundColor: theme.colors.accent }]}
      onPress={handleRematch}
      disabled={creating}>
      {creating ? (
        <ActivityIndicator size="small" color={theme.colors.accentForeground} />
      ) : (
        <>
          <Swords size={18} color={theme.colors.text} />
          <Text style={[styles.text, { color: theme.colors.text }]}>Rematch</Text>
        </>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 0,
  },
  text: {
    fontSize: 15,
    fontWeight: '700',
  },
});
